import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import addContacts from '@/shared/api/bookings/services/addContacts';
import createBookingService from '@/shared/api/bookings/services/createBookingService';
import removeContacts from '@/shared/api/bookings/services/removeContacts';
import updateService from '@/shared/api/bookings/services/updateService';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import BookingsService from '@/shared/types/bookings/service.type';
import DialogName from '@/shared/types/dialogName.enum';

import BookingContactForm from '../types/bookingContactForm.type';

type OnUpdateDialogState = ReturnType<
  typeof useModalDialog<BookingsService, DialogName>
>['onUpdateDialogState'];

type DialogType = ReturnType<
  typeof useModalDialog<BookingsService, DialogName>
>['dialog'];

type OnUpdateNotificationBar = ReturnType<
  typeof useNotificationBar
>['onUpdateNotificationBar'];

type OnUpdateProgressDialog = ReturnType<
  typeof useProgressDialog
>['onUpdateProgressDialog'];

type Args = {
  dialog: DialogType;
  onUpdateDialogState: OnUpdateDialogState;
  onUpdateNotificationBar: OnUpdateNotificationBar;
  onUpdateProgressDialog: OnUpdateProgressDialog;
};

const useBookingService = ({
  dialog,
  onUpdateDialogState,
  onUpdateNotificationBar,
  onUpdateProgressDialog,
}: Args) => {
  const redirectLogin = useRedirectLogin();
  const { t } = useTranslation(['bookingsServices', 'common']);

  const [refreshTableIndex, setRefreshTableIndex] = useState(0);

  const handleUpdateBookingService = async (
    formValues: Partial<BookingsService>,
  ) => {
    onUpdateDialogState(DialogName.Edit, { loading: true });
    onUpdateProgressDialog({
      open: true,
      title: t('bookingsServices:updatingBookingService'),
      progress: 0,
    });

    const response = await updateService({
      body: formValues,
      id: dialog?.edit?.data?.booking_service_id || '',
      onUnauthorized: () => redirectLogin(),
    });

    if (response.success) {
      onUpdateDialogState(DialogName.Edit, { loading: false, open: false });
      onUpdateNotificationBar({
        open: true,
        message: t('bookingsServices:bookingServiceWasSuccessfullyUpdated'),
        severity: 'success',
      });
      onUpdateProgressDialog({ open: false });
      setRefreshTableIndex((prev) => prev + 1);
    } else {
      onUpdateProgressDialog({ open: false });
      onUpdateDialogState(DialogName.Edit, {
        loading: false,
        errors: [
          response.msg || t('bookingsServices:bookingServiceUpdateError'),
        ],
      });
    }
  };

  const handleAddContactsToBookingService = async (
    formValues: BookingContactForm[],
  ) => {
    onUpdateDialogState(DialogName.AddContacts, {
      loading: true,
    });
    onUpdateProgressDialog({
      open: true,
      title: t('bookingsServices:addingContacts'),
      progress: 0,
    });

    const response = await addContacts({
      body: formValues,
      id: dialog?.addContacts?.data?.booking_service_id || '',
      onUnauthorized: () => redirectLogin(),
      onProgress(progress) {
        onUpdateProgressDialog({ progress });
      },
    });

    if (response.every((res) => res.success)) {
      onUpdateDialogState(DialogName.AddContacts, {
        loading: false,
        open: false,
      });
      onUpdateNotificationBar({
        open: true,
        message: t('bookingsServices:contactsWereSuccessfullyAdded'),
        severity: 'success',
      });
      onUpdateProgressDialog({ open: false });
      setRefreshTableIndex((prev) => prev + 1);
    } else {
      onUpdateProgressDialog({ open: false });
      setRefreshTableIndex((prev) => prev + 1);
      onUpdateDialogState(DialogName.AddContacts, {
        loading: false,
        errors: response.filter((res) => !res.success).map((res) => res.msg),
      });
    }
  };

  const handleRemoveContactsFromBookingService = async (
    selectedContactsIds: string[],
  ) => {
    const selectedContactsAsObjects = selectedContactsIds.map((id) => ({
      booking_service_contact_id: id,
    }));

    onUpdateDialogState(DialogName.RemoveContacts, {
      loading: true,
    });
    onUpdateProgressDialog({
      open: true,
      title: t('bookingsServices:removingContacts'),
      progress: 0,
    });

    const response = await removeContacts({
      bookingServiceId: dialog?.removeContacts?.data?.booking_service_id || '',
      body: selectedContactsAsObjects,
      onUnauthorized: () => redirectLogin(),
      onProgress(progress) {
        onUpdateProgressDialog({ progress });
      },
    });

    if (response.every((res) => res.success)) {
      onUpdateDialogState(DialogName.RemoveContacts, {
        loading: false,
        open: false,
      });
      onUpdateNotificationBar({
        open: true,
        message: t('bookingsServices:contactsWereSuccessfullyRemoved'),
        severity: 'success',
      });
      setRefreshTableIndex((prev) => prev + 1);
      onUpdateProgressDialog({ open: false });
    } else {
      onUpdateProgressDialog({ open: false });
      setRefreshTableIndex((prev) => prev + 1);
      onUpdateDialogState(DialogName.RemoveContacts, {
        loading: false,
        errors: response.filter((res) => !res.success).map((res) => res.msg),
      });
    }
  };

  const handleCreateBookingService = async (values: {
    bookingServiceDetails: Partial<BookingsService>;
    bookingContactsDetails: BookingContactForm[];
  }) => {
    onUpdateDialogState(DialogName.CreateBookingService, { loading: true });
    onUpdateProgressDialog({
      open: true,
      title: t('bookingsServices:creatingBookingService'),
      progress: 0,
    });

    const bookingServiceResponse = await createBookingService(
      values.bookingServiceDetails,
      () => redirectLogin(),
    );

    if (bookingServiceResponse.success) {
      const { data } = bookingServiceResponse;

      const addContactsResponse = await addContacts({
        body: values.bookingContactsDetails,
        id: data.booking_service_id,
        onUnauthorized: () => redirectLogin(),
        onProgress(progress) {
          onUpdateProgressDialog({ progress });
        },
      });

      if (addContactsResponse.every((res) => res.success)) {
        onUpdateDialogState(DialogName.CreateBookingService, {
          loading: false,
          open: false,
        });
        onUpdateNotificationBar({
          open: true,
          message: t('bookingsServices:bookingServiceWasSuccessfullyCreated'),
          severity: 'success',
        });
        onUpdateProgressDialog({ open: false });
        setRefreshTableIndex((prev) => prev + 1);
      } else {
        onUpdateProgressDialog({ open: false });
        onUpdateDialogState(DialogName.CreateBookingService, {
          loading: false,
          errors: addContactsResponse
            .filter((res) => !res.success)
            .map((res) => res.msg),
        });
      }
    } else {
      onUpdateProgressDialog({ open: false });
      onUpdateDialogState(DialogName.CreateBookingService, {
        loading: false,
        errors: [
          bookingServiceResponse.msg ||
            t('bookingsServices:bookingServiceCreationError'),
        ],
      });
    }
  };

  return {
    refreshTableIndex,
    handleUpdateBookingService,
    handleAddContactsToBookingService,
    handleRemoveContactsFromBookingService,
    handleCreateBookingService,
  };
};

export default useBookingService;
